import { ChangeEvent, FC, useEffect, useState } from 'react';

import { DATE_FORMAT_ISO, parse } from '@eneco/common/date';
import { capitalizeFirstLetter } from '@eneco/common/string';
import RichText from '@eneco/components/RichText/RichText';
import { TrackedDialog } from '@eneco/components/TrackedDialog/TrackedDialog';
import useDC from '@eneco/dc/useDC';
import { StepHeader, Layout, NextButton, Overview } from '@eneco/flows/components';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { typedYupResolver } from '@eneco/hooks/form';
import { useFormatter } from '@eneco/i18n';
import { Box, Heading, InputText, Stack, Text, TextLink } from '@eneco/sparky';
import { usePlaceholderContent } from '@sitecore/common';
import { SummaryStepRendering } from '@sitecore/types/SummaryStep';
import { useMachine } from '@xstate/react';
import { friendlyFormatIBAN, isValidIBAN } from 'ibantools';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { ServiceGemakFlowError, TermsAndConditions } from '../../components';
import { FlowContextServiceGemak } from '../../context';
import { selectCostDetailsByType } from '../../helpers/dc';
import {
  getNonCommodityOffer,
  getOfferTermsAndConditions,
  getSelectProductCombinationByMarketingProductCode,
} from '../../helpers/dc.nonCommodity';
import { useFlowTracking } from '../../hooks/useFlowTracking';
import { offerMachineNonCommodity, orderMachine } from '../../machines';

interface FormFields {
  iban?: string;
}
export const SummaryStep: FC = () => {
  const { useFlowActorRef, useFlowSelector } = useFlowHooks<FlowContextServiceGemak>();
  const sendToFlowMachine = useFlowActorRef().send;
  const [orderState, sendToOrderMachine] = useMachine(orderMachine);
  const [offerState, sendToOfferMachine] = useMachine(offerMachineNonCommodity);
  const {
    context: { orderTypes: switchTypes },
  } = orderState;

  const flowContext = useFlowSelector(state => state.context);
  const {
    firstName,
    surname,
    birthdate,
    postalCode,
    houseNumber,
    houseNumberSuffix,
    street,
    city,
    iban,
    phoneNumber,
    emailAddress,
    isSME,
    isPersonalDetailsEditProhibited,
  } = flowContext;

  const { SummaryStep: { fields: textualData } = {} } = usePlaceholderContent<{
    SummaryStep: SummaryStepRendering;
  }>();

  const offer = offerState?.context?.offer;

  const formSchema = yup.object({
    ...(!isPersonalDetailsEditProhibited && {
      iban: yup
        .string()
        .required(textualData?.paymentDetails?.ibanFormField?.value?.requiredMessage)
        .test(
          'iban validation',
          textualData?.paymentDetails?.ibanFormField?.value?.validationMessage ?? '',
          iban => !!(iban && isValidIBAN(iban.replace(/\s+/g, '').toUpperCase())),
        ),
    }),
  });

  const { businessUnit, label } = useDC();
  const { trackPurchase } = useFlowTracking();
  const { date, address } = useFormatter();

  const [isLoading, setIsLoading] = useState(true);
  const [isSuccessful, setIsSuccessful] = useState(false);
  const [isFailed, setIsFailed] = useState(false);

  const [isNotFound, setIsNotFound] = useState(false);
  const [isNotAvailable, setIsNotAvailable] = useState(false);
  const [isNotAuthenticated, setIsNotAuthenticated] = useState(false);

  const [isOrderFailed, setIsOrderFailed] = useState(false);

  const selectedOffer = getNonCommodityOffer(flowContext, flowContext?.sgProduct ?? '', offer);
  const selectedProduct = getSelectProductCombinationByMarketingProductCode(
    offer?.productCombinations ?? [],
    flowContext?.sgProduct ?? '',
  );
  const selectedOfferTermsAndConditions = getOfferTermsAndConditions(flowContext, offer);
  const costsType = isSME ? 'vatExcluded' : 'vatIncluded';

  const {
    handleSubmit,
    getValues,
    register,
    formState: { errors, isSubmitSuccessful },
  } = useForm<FormFields>({
    mode: 'onSubmit',
    defaultValues: { iban },
    resolver: typedYupResolver<FormFields>(formSchema),
  });

  useEffect(() => {
    sendToOfferMachine({
      type: 'GET_OFFER',
      config: {
        businessUnit,
        label,
      },
      values: { ...flowContext },
      offerType: 'ServiceGemak',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- lifecycle componentDidMount
  }, []);

  useEffect(() => {
    if (isSubmitSuccessful) {
      sendToOrderMachine({
        type: 'SEND_ORDER',
        config: {
          businessUnit,
          label,
        },
        values: { ...flowContext, ...getValues() },
        offer: offerState.context.offer ?? { isMkb: !!isSME },
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitSuccessful]);

  useEffect(() => {
    setIsOrderFailed(orderState.matches('ERROR'));

    if (orderState.matches('SUCCESS')) {
      selectedOffer && trackPurchase(selectedOffer, offer, switchTypes);

      sendToFlowMachine({
        type: 'NEXT_PAGE',
        values: { ...getValues() },
      });
    }

    if (orderState.matches('ERROR')) {
      setIsSuccessful(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderState]);

  useEffect(() => {
    if (!offerState.matches('IDLE')) {
      setIsLoading(offerState.matches('FETCHING'));
      setIsFailed(!offerState.matches('FETCHING') && !offerState.matches('SUCCESS'));
      setIsSuccessful(offerState.matches('SUCCESS'));

      setIsNotFound(offerState.matches('ERROR_NOT_FOUND'));
      setIsNotAvailable(offerState.matches('ERROR_NOT_AVAILABLE'));
      setIsNotAuthenticated(offerState.matches('ERROR_NOT_AUTHENTICATED'));
    }
  }, [offerState]);

  const renderBankingInformationSection = () => (
    <>
      <Stack.Item>
        <Stack direction="column" gap="4">
          <Heading as="h3" size="XS">
            {textualData?.paymentDetails?.title?.value}
          </Heading>
          <Text>{textualData?.paymentDetails?.description?.value}</Text>
        </Stack>
      </Stack.Item>

      <Stack.Item>
        <InputText
          {...register('iban', {
            onChange: (event: ChangeEvent<HTMLInputElement>) => {
              const target = event.target;

              const caretPosition = target.selectionStart;

              const currentValue = target.value;
              const newValue = friendlyFormatIBAN(currentValue) ?? '';

              target.value = newValue;
              target.selectionEnd = currentValue.length !== newValue.length ? (caretPosition ?? 0) + 1 : caretPosition;
            },
          })}
          label={textualData?.paymentDetails?.ibanFormField?.value?.label ?? ''}
          hint={textualData?.paymentDetails?.ibanFormField?.value?.hint}
          placeholder={textualData?.paymentDetails?.ibanFormField?.value?.placeholder}
          error={errors.iban?.message}
        />

        <Box paddingTop="3">
          <Text size="BodyS" color="textLowEmphasis">
            <TrackedDialog
              title={textualData?.paymentDetails?.ibanDialog?.value?.title}
              trigger={<TextLink>{textualData?.paymentDetails?.ibanDialog?.value?.triggerText}</TextLink>}>
              <RichText html={textualData?.paymentDetails?.ibanDialog?.value?.content} />
            </TrackedDialog>
          </Text>
        </Box>
      </Stack.Item>
    </>
  );

  const renderOverviewFooter = () => {
    return (
      <Box paddingX={6}>
        <Text size="BodyS" color="textLowEmphasis">
          <Stack direction="column" gap="1">
            <Stack direction="row" gap="1" alignX="justify">
              <Text>{textualData?.productsOverview?.contractStartDateLabel?.value}</Text>
              <Text>{offer?.contractStartDate && date.short(parse(offer.contractStartDate, DATE_FORMAT_ISO))}</Text>
            </Stack>

            {selectedOffer?.contractEndDate && (
              <Stack direction="row" gap="1" alignX="justify">
                <Text>{textualData?.productsOverview?.contractEndDateLabel?.value}</Text>
                <Text>{date.short(parse(selectedOffer.contractEndDate, DATE_FORMAT_ISO))}</Text>
              </Stack>
            )}
          </Stack>
        </Text>
      </Box>
    );
  };

  const renderPersonalDetails = () => {
    return (
      <Overview.Details
        type="profile"
        title={textualData?.personalDetailsOverview?.personalDetailsLabel?.value ?? ''}
        editButton={{
          label: textualData?.personalDetailsOverview?.editDetailsLabel?.value ?? '',
          onClick: () => sendToFlowMachine({ type: 'GOTO', stepName: 'STEP_PERSONAL_DETAILS' }),
        }}>
        <Text as="p">
          {textualData?.personalDetailsOverview?.nameLabel?.value}: {`${firstName} ${surname}`}
        </Text>
        <Text as="p">
          {textualData?.personalDetailsOverview?.birthDateLabel?.value}: {birthdate}
        </Text>
        <Text as="p">
          {textualData?.personalDetailsOverview?.phoneNumberLabel?.value}: {phoneNumber}
        </Text>
        <Text as="p">
          {textualData?.personalDetailsOverview?.emailAddressLabel?.value}: {emailAddress}
        </Text>
      </Overview.Details>
    );
  };

  const renderAddressDetails = () => {
    return (
      <Overview.Details
        type="address"
        title={textualData?.personalDetailsOverview?.addressDetailsLabel?.value ?? ''}
        editButton={{
          label: textualData?.personalDetailsOverview?.editDetailsLabel?.value ?? '',
          onClick: () => sendToFlowMachine({ type: 'GOTO', stepName: 'STEP_ADDRESS' }),
        }}>
        <Text as="p">{address.streetAddress({ street, houseNumber, houseNumberSuffix })}</Text>
        <Text as="p">{address.postalCodeCity({ postalCode, city })}</Text>
      </Overview.Details>
    );
  };

  const getErrorNotification = () => {
    if (isNotAuthenticated) return textualData?.genericError?.errorNotAuthenticatedNotification;
    if (isNotAvailable) return textualData?.genericError?.errorNotAvailableNotification;
    if (isNotFound) return textualData?.genericError?.errorNotFoundNotification;
    if (isOrderFailed) return textualData?.genericError?.sendOrderErrorNotification;

    return textualData?.genericError?.fetchOfferErrorNotification;
  };

  const getErrorButtonLabel = () => {
    if (isNotAuthenticated) return textualData?.genericError?.loginButtonText;
    if (!isNotAvailable && !isNotFound) return textualData?.genericError?.tryAgainButtonText;

    return undefined;
  };

  return (
    <Layout.Content variant="B" isLoading={isLoading} handleSubmit={handleSubmit}>
      {isSuccessful && (
        <>
          <StepHeader textualData={textualData} />
          <Stack.Item>
            <Overview>
              <Overview.Header>{textualData?.personalDetailsOverview?.title?.value}</Overview.Header>
              {renderPersonalDetails()}
              {renderAddressDetails()}
            </Overview>
          </Stack.Item>

          <Stack.Item>
            <Overview>
              <Overview.Header>{textualData?.productsOverview?.title?.value}</Overview.Header>
              {selectedProduct?.products?.map(product => {
                const productData = offer?.products?.find(p => p.marketingProductCode === product.marketingProductCode);
                return (
                  <Overview.Product
                    key={selectedProduct?.discountCode}
                    type="boiler"
                    hideIcon={false}
                    title={capitalizeFirstLetter(productData?.productType ?? '')}
                    subtitle={productData?.description ?? ''}
                    costsMonthly={{
                      value: selectCostDetailsByType('monthlyCosts', selectedProduct?.costDetails)?.[costsType] ?? 0,
                      period: textualData?.costDetails?.perMonthLabel?.value,
                    }}
                  />
                );
              })}

              {renderOverviewFooter()}
            </Overview>
          </Stack.Item>

          {!isPersonalDetailsEditProhibited && renderBankingInformationSection()}

          <TermsAndConditions textualData={textualData} offerTermsAndConditions={selectedOfferTermsAndConditions} />

          <NextButton isLoading={isSubmitSuccessful}>{textualData?.content?.nextStepText?.value}</NextButton>
        </>
      )}

      {(isFailed || isOrderFailed) && (
        <ServiceGemakFlowError
          type={isNotAuthenticated ? 'AUTHENTIFICATION' : 'GENERIC'}
          textualData={{
            notification: getErrorNotification(),
            buttonLabel: getErrorButtonLabel(),
          }}
        />
      )}
    </Layout.Content>
  );
};
